import { animate, style, transition, trigger } from '@angular/animations';
import { LiveAnnouncer } from '@angular/cdk/a11y';
import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, Output, ViewEncapsulation } from '@angular/core';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { SortDirection } from '@angular/material/sort';
import { DataSet, Record as DataCoreRecord } from '@mtna/data-core-ts';
import { UpdateQueryParameters } from '@mtna/data-query-ts';
import { DURATION_MEDIUM_COLLAPSE, EASING_STANDARD, MtnaResizableTable, MtnaSortedColumn } from '@mtna/core-angular';
import { trackByCoreResource, Variable } from '@mtna/pojo-consumer-ts';
import { RdsCodeDisplayType } from '@rds/shared-api';

import { RdsRecordUtil } from '../core/utils/record-util';
import { RdsGetRecordValuePipe } from './get-record-value.pipe';

export const RDS_SELECT_TABLE_MIN_COL_WIDTH = 50;
export const RDS_SELECT_TABLE_MAX_COL_WIDTH = 400;

export const rdsEditableColumnFieldTypeIDs = ['CHECK'] as const;
export type RdsEditableColumnFieldType = typeof rdsEditableColumnFieldTypeIDs[number];

export class RdsSelectTableEditEvent {
  constructor(public recordLayoutUri: string, public update: UpdateQueryParameters) {}
}

/**
 * Custom mat-table used to display the results of a RDS select query
 *
 * @author Will Davis <will.davis@mtna.us>
 */
@Component({
  selector: 'rds-c-select-table',
  templateUrl: './select-table.component.html',
  styleUrls: ['./select-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  animations: [
    trigger('rowsAnimation', [
      transition(':enter', [style({ opacity: 0 }), animate(`${DURATION_MEDIUM_COLLAPSE} ${EASING_STANDARD}`, style({ opacity: 1 }))]),
    ]),
  ],
})
export class RdsSelectTableComponent extends MtnaResizableTable<DataCoreRecord> {
  static ngAcceptInputType_ghost: BooleanInput;
  static ngAcceptInputType_inspectDisabled: BooleanInput;
  static ngAcceptInputType_selectableHeaders: BooleanInput;
  static ngAcceptInputType_showPrimaryKeyColumns: BooleanInput;
  static ngAcceptInputType_sortedColumns: MtnaSortedColumn[] | null | undefined;
  static ngAcceptInputType_enableSort: BooleanInput;
  static ngAcceptInputType_toggledAreSelected: BooleanInput;

  /** Display configuration for codes in the table. Defaults to ‘NAME’ */
  @Input() codeDisplayType: RdsCodeDisplayType = 'NAME';
  /** The columns that have been used as a filter */
  @Input() set columnsWithFilters(variables: Array<Variable>) {
    this._appliedFilters = {};
    variables.forEach((v) => (this._appliedFilters[v.uri || ''] = true));
  }
  /** The result of the RDS select query to display in the table */
  @Input()
  get dataSet(): DataSet {
    return this._dataSet;
  }
  set dataSet(ds: DataSet) {
    this._dataSet = ds;
    // Reset arrays and maps when DataSet changes
    this._editableColumns = this.getDefaultEditableColumns();
    this._lockedColumns = {};
    this._keyColumns = {};
    this._lastLockedVariable = null;

    // determine and set displayable columns for the table
    if (!this.dataSet) {
      this.displayedColumns = [];
      this._readOnlyVariables = [];
    } else {
      // Setup the locked variable map
      if (!!this.dataSet.dataSetInformation && !!this.dataSet.dataSetInformation.lockedVariableUris) {
        this.dataSet.dataSetInformation.lockedVariableUris.forEach((uri) => (this._lockedColumns[uri] = true));
        // Set the last locked variable
        this._lastLockedVariable =
          this.dataSet.dataSetInformation.lockedVariableUris[this.dataSet.dataSetInformation.lockedVariableUris.length - 1];
      }

      // Setup the key variable map
      if (!!this.dataSet.recordLayout && !!this.dataSet.recordLayout.primaryKey && !!this.dataSet.recordLayout.primaryKey.variables) {
        this.dataSet.recordLayout.primaryKey.variables.forEach((variable) => (this._keyColumns[variable.uri || ''] = true));
      }

      // Initialize displayed columns after key map is set,
      // this is required to determine what columns are displayed
      this.initializeDisplayedColumns();

      const editableFields: { [uri: string]: Variable } = {};

      // If the dataset has an editConfiguration,
      // Populate an object map of editable variables
      if (
        !!this.dataSet.editConfiguration &&
        !!this.dataSet.editConfiguration.editableFields &&
        !!this.dataSet.editConfiguration.editableFields.length
      ) {
        this.dataSet.editConfiguration.editableFields.forEach((field) => {
          // Find the variable in the record layout
          const editableVariable = this.dataSet.recordLayout.variables.find((v) => v.uri === field.variableUri);
          // Set the editable fields map
          if (editableVariable) {
            editableFields[field.variableUri] = editableVariable;
          }

          /* Determine the specific control for the edit field */
          if (editableVariable?.dataType === 'BOOLEAN') {
            // A boolean is represented by a CHECKBOX
            const existingCheckboxColumns = this._editableColumns.CHECK || [];
            this._editableColumns.CHECK = [...existingCheckboxColumns, editableVariable];
          }
        });
      }

      // Populate the array of readonly variables by filtering out
      // the variables that exist in the editConfiguration
      this._readOnlyVariables = this.dataSet.recordLayout.variables.filter((v) => !editableFields[v.uri || '']);
    }

    if (this.resizableColumns) {
      this.initColumnWidths(ds);
    }
  }
  private _dataSet!: DataSet;

  /**
   * Whether ghost elements should be rendered to show a loading state.
   * A fake/empty data set should be provided so no values show in the table.
   */
  @Input()
  get ghost(): boolean {
    return this._ghost;
  }
  set ghost(value: boolean) {
    this._ghost = coerceBooleanProperty(value);
  }
  private _ghost = false;

  /** Indicates which column is inspected */
  @Input() inspectedVariable: Variable | null | undefined;

  /** Whether the header can be clicked to inspect a variable. */
  @Input()
  get inspectDisabled(): boolean {
    return this._inspectDisabled;
  }
  set inspectDisabled(i: boolean) {
    this._inspectDisabled = coerceBooleanProperty(i);
  }
  private _inspectDisabled = false;

  /** Whether to show the columns that are used as primary keys, defaults to true */
  @Input()
  get showPrimaryKeyColumns(): boolean {
    return this._showPrimaryKeyColumns;
  }
  set showPrimaryKeyColumns(value: boolean) {
    const newValue = coerceBooleanProperty(value);
    if (this.showPrimaryKeyColumns !== newValue) {
      this._showPrimaryKeyColumns = newValue;
      // Re-initialize the displayed columns if the DataSet has already been set
      if (this.dataSet) {
        this.initializeDisplayedColumns();
      }
      this.cdr.markForCheck();
    }
  }
  private _showPrimaryKeyColumns = true;

  /** The columns currently being sorted, in order of priority. */
  @Input()
  get sortedColumns(): MtnaSortedColumn[] {
    return this._sortedColumns;
  }
  set sortedColumns(c: MtnaSortedColumn[]) {
    this._sortedColumns = c || [];
  }
  private _sortedColumns: MtnaSortedColumn[] = [];

  /** Whether to enable sorting */
  @Input()
  get enableSort(): boolean {
    return this._enableSort;
  }
  set enableSort(s: boolean) {
    this._enableSort = coerceBooleanProperty(s);
  }
  private _enableSort = false;

  /** Whether the header is selectable. Defaults to false. */
  @Input()
  get selectableHeaders(): boolean {
    return this._selectableHeaders;
  }
  set selectableHeaders(value: boolean) {
    this._selectableHeaders = coerceBooleanProperty(value);
  }
  private _selectableHeaders = false;

  /**
   * Whether the toggled columns represent the selected, or unselected.
   * For instance, if the user selects 100+ columns, and unselects 5,
   * the 'toggled' columns will be the 5 unselected & 'toggledAreSelected' will be false;
   * i.e.
   * user selects 95 out of 100 => toggledAreSelected: false, toggled.length: 5;;
   * user selects 5 out of 100 => toggledAreSelected: true, toggled.length: 5;
   */
  @Input()
  get toggledAreSelected(): boolean {
    return this._toggledAreSelected;
  }
  set toggledAreSelected(value: boolean) {
    this._toggledAreSelected = coerceBooleanProperty(value);
  }
  private _toggledAreSelected = true;

  /** The columns that are toggled for including/excluding in packaging */
  @Input() set toggledColumns(variables: Array<Variable>) {
    this._toggledColumns = {};
    variables.forEach((v) => (this._toggledColumns[v.uri || ''] = true));
  }

  /** Emitted when record has been edited */
  @Output() edit = new EventEmitter<RdsSelectTableEditEvent>();
  /** Emitted when a column is inspectected/clicked */
  @Output() inspectVariable = new EventEmitter<Variable>();
  /** Emitted when a sort is triggered on a column. */
  @Output() sort = new EventEmitter<MtnaSortedColumn>();

  get areColumnsSet(): boolean {
    return !!this.dataSet;
  }

  get maxAutoColWidth(): number {
    return RDS_SELECT_TABLE_MAX_COL_WIDTH;
  }

  get minColWidth(): number {
    return RDS_SELECT_TABLE_MIN_COL_WIDTH;
  }

  /** Map of variable uris applied as filters, used for decorators */
  _appliedFilters: { [uri: string]: boolean } = {};
  /** Map of primary key variable uris, used for decorators */
  _keyColumns: { [uri: string]: boolean } = {};
  /** Last locked variable */
  _lastLockedVariable: string | null = null;
  /** Map of locked variable uris, used for decorators */
  _lockedColumns: { [uri: string]: boolean } = {};
  /** The non-editable variables */
  _readOnlyVariables: Array<Variable> = [];
  /** Map of toggled variable uris to indicate which have been included/excluded for packaging, used for decorators */
  _toggledColumns: { [uri: string]: boolean } = {};
  /** TrackBy function for the column for loop */
  _trackByCr = trackByCoreResource;
  /** Variables split up into table columns by type */
  _editableColumns: Record<RdsEditableColumnFieldType, Variable[]> = this.getDefaultEditableColumns();

  _headerSortButtonTooltip = $localize`Sorting based on code value, not display label`;

  constructor(cdr: ChangeDetectorRef, private liveAnnouncer: LiveAnnouncer, private recordValuePipe: RdsGetRecordValuePipe) {
    super(cdr);
  }

  handleCheckChange(event: MatCheckboxChange, record: DataCoreRecord, variable: Variable) {
    this.edit.emit(
      new RdsSelectTableEditEvent(this.dataSet.recordLayout.uri || '', {
        variableUri: variable.uri || '',
        value: event.checked,
        key: RdsRecordUtil.getKey(record, this.dataSet.recordLayout.primaryKey),
      })
    );
  }

  handleInspectVariable(variable: Variable) {
    // prevent selection while resizing
    if (!this._resizingColumn) {
      this.inspectVariable.emit(variable);
    }
  }

  handleSortColumn(direction: SortDirection, id: string, label?: string) {
    const columnName = label || $localize`:noun|:column`;
    let message: string;
    switch (direction) {
      case 'asc':
        message = $localize`Sorting ${columnName}:column: ascending.`;
        break;
      case 'desc':
        message = $localize`Sorting ${columnName}:column: descending.`;
        break;
      default:
        message = $localize`Sorting ${columnName}:column: removed.`;
        break;
    }
    this.liveAnnouncer.announce(message);
    this.sort.emit({ direction, id });
  }

  getLargestContentWidth(id: string): number {
    const variable = this.dataSet.recordLayout.variables.find((v) => v.uri === id);
    // Run through the records & find the largest length
    let contentWidth = 0;
    if (variable) {
      this.dataSet.records.forEach((r) => {
        const value = this.recordValuePipe.transform(this.codeDisplayType, r, variable);
        contentWidth = Math.max(contentWidth, this.calculateCellWidth(`${value || ''}`.length));
      });
    }
    return contentWidth;
  }

  protected getColumnWidth(id: string | null | undefined): number {
    const variable = this.dataSet.recordLayout.variables.find((v) => v.uri === id);
    if (!!variable && ((variable.dataType === 'NUMERIC' && !variable.classificationUri) || variable.dataType === 'GHOST')) {
      return 100;
    }
    return super.getColumnWidth(id);
  }

  protected getDefaultEditableColumns(): Record<RdsEditableColumnFieldType, Variable[]> {
    return { CHECK: [] };
  }

  /**
   * Initialize the displayed columns for the table.
   * Take into account whether priamry keys should be hidden.
   */
  protected initializeDisplayedColumns() {
    const allVariableUris = this.dataSet.recordLayout.variables.map((v) => v.uri as string);

    this.displayedColumns = this.showPrimaryKeyColumns ? allVariableUris : allVariableUris.filter((uri) => !this._keyColumns[uri]);
  }

  protected initColumnWidths(dataset: DataSet) {
    if (dataset) {
      this.dataSet.recordLayout.variables.forEach((v) => {
        if (!this._columnWidth[v.uri as string]) {
          this._columnWidth[v.uri as string] = v.name
            ? Math.max(this.calculateCellWidth(v.name.length), 150)
            : this.getColumnWidth(v.uri as string); // 1 char = 8px + 24px (padding)
        }
      });
    } else {
      super.initializeColumnWidths();
    }
  }
}
